﻿import React, { useState, useMemo, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Settings, Sun, Moon, BellRing, Plus, User, LogOut, Mail } from 'lucide-react';
import { useConfig } from '@/context/ConfigContext';
import { useUser } from '@/context/UserContext';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { getStatusColor, getDisplayColor } from '@/lib/statusColors';
import { SettingsModal } from '@/components/SettingsModal';
import { ProfileSettingsModal } from '@/components/ProfileSettingsModal';
import { ProfileSelector } from '@/components/ProfileSelector';
import { PinDialog } from '@/components/PinDialog';
import { AdminPinRecoveryDialog } from '@/components/AdminPinRecoveryDialog';
import { ProfilePinRecoveryDialog } from '@/components/ProfilePinRecoveryDialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

import { RouteStatusBar } from '@/components/RouteStatusBar';
import { PlanStatusBadge } from '@/components/PlanStatusBadge';
import { MiniWhatsAppButton } from '@/components/ProfileActionBar';
import { InternalCompanySearchModal } from '@/components/InternalCompanySearchModal';
import VanLightPng from '@/assets/perfiles-icono.png';
import VanDarkPng from '@/assets/perfiles-icono1.png';
import VanLightOnPng from '@/assets/perfiles-iconoON.png';
import VanDarkOnPng from '@/assets/perfiles-icono1ON.png';
import MaskPng from '@/assets/perfiles-icono-mask.png';
import { auth, db } from '@/firebase';
import { registerTrustedDevice } from '@/lib/trustedDeviceSync';
import { getWorkspaceId } from '@/lib/workspaceUtils';
import { getFunctions, httpsCallable } from 'firebase/functions';

export function AppHeader({
  currentDate,
  currentDateDisplay,
  onDateChange,
  routeStatus,
  onRouteStatusChange,
  colorCodeEnabled = true,
  statusLabels,
  requireConfirmation = false,
  registerProfilePinDialogOpener,
  onAlertClick,
  showAlertButton,
  userRole,
}) {
  const {
    config,
    workspaceId,
    updateProfilesConfig,
    updateOwnWorkerProfile,
    adminPinHash,
    adminPinSalt,
    securityReady,
    updateAdminSecurityConfig,
    assignedProfileIds,
  } = useConfig();

  const {
    selectedUser,
    setSelectedUser,
    switchProfile,
    isProfileLocked,
    isAdminUnlocked,
    unlockAdmin,
    tryUnlockWithTrustedDevice,
    trustedDeviceChecking,
  } = useUser();
  const { theme, toggleTheme, isInitialized } = useTheme();
  const { permissions } = useSubscription();
  const { handleLogout } = useAuth();
  const { toast } = useToast();
   const statusColors = (config?.routeStatesConfig?.colors) || { go: 'green', salimos: 'blue', volvemos: 'yellow', fin: 'red' };

  const VanPng = theme === 'dark' ? VanDarkPng : VanLightPng;
   const [isSettingsOpen, setIsSettingsOpen] = useState(false);
   const [isAccountOpen, setIsAccountOpen] = useState(false);
   const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
   const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
   const [isPinDialogOpen, setIsPinDialogOpen] = useState(false);
   const [pinDialogProfile, setPinDialogProfile] = useState(null);
   const [isAdminPinDialogOpen, setIsAdminPinDialogOpen] = useState(false);
   const [isAdminPinRecoveryDialogOpen, setIsAdminPinRecoveryDialogOpen] = useState(false);
   const [isProfilePinRecoveryDialogOpen, setIsProfilePinRecoveryDialogOpen] = useState(false);
   const [isProfileSelectorOpen, setIsProfileSelectorOpen] = useState(false);
   const [workerProfilesInfo, setWorkerProfilesInfo] = useState(undefined);
   const [isCompanySearchOpen, setIsCompanySearchOpen] = useState(false);




  // Registrar callback para abrir PinDialog desde overlay bloqueado
  useEffect(() => {
    if (!registerProfilePinDialogOpener) return;
    
    const pinOpener = (profile) => {
      setPinDialogProfile(profile);
      setIsPinDialogOpen(true);
    };
    
    registerProfilePinDialogOpener(pinOpener);
    
    return () => {
      registerProfilePinDialogOpener(null);
    };
  }, [registerProfilePinDialogOpener]);

  const handlePreviousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    onDateChange(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    onDateChange(newDate);
  };

   // Obtener perfiles informativos solo para trabajadores
   useEffect(() => {
     if (userRole !== 'worker') {
       return;
     }

     let isMounted = true;

     const fetchWorkerProfiles = async () => {
       try {
         const functions = getFunctions();
         const callable = httpsCallable(functions, 'getWorkerProfilesInfo');
           const result = await callable();
           const profiles = Array.isArray(result.data?.profiles) ? result.data.profiles : [];
           if (isMounted) {
            setWorkerProfilesInfo(profiles);
          }
       } catch (err) {
         console.warn('[AppHeader] getWorkerProfilesInfo failed:', err);
         if (isMounted) {
           setWorkerProfilesInfo([]);
         }
       }
     };

     fetchWorkerProfiles();

     return () => {
       isMounted = false;
     };
   }, [userRole]);

   // Filtro: solo mostrar perfiles activos o legacy (sin status)
    const isActiveProfile = (profile) => (profile.status ?? 'active') === 'active';
    const visibleProfiles = config.profiles.filter(isActiveProfile);

   // Combinar perfiles: operativos + informativos adicionales (solo para workers)
   const selectorProfiles = useMemo(() => {
     let baseProfiles;
     
     if (userRole !== 'worker' || !Array.isArray(workerProfilesInfo)) {
       baseProfiles = visibleProfiles;
     } else {
       const assignedIds = new Set(visibleProfiles.map(p => String(p.id ?? p.name)));
       const additionalProfiles = workerProfilesInfo
         .filter(p => !assignedIds.has(String(p.id ?? p.name)))
         .map(p => ({
           ...p,
           isInformational: true
         }));
       baseProfiles = [...visibleProfiles, ...additionalProfiles];
     }

     // Reordenar: si existe assignedProfileIds[0], ponerlo primero
     if (assignedProfileIds?.length > 0) {
       const assignedProfileId = String(assignedProfileIds[0]);
       const assignedProfileIndex = baseProfiles.findIndex(p => String(p.id ?? p.name) === assignedProfileId);
       
       if (assignedProfileIndex > 0) {
         // Extraer el perfil asignado y ponerlo al inicio
         const assignedProfile = baseProfiles[assignedProfileIndex];
         const reordered = [
           assignedProfile,
           ...baseProfiles.slice(0, assignedProfileIndex),
           ...baseProfiles.slice(assignedProfileIndex + 1)
         ];
         return reordered;
       }
     }
     
     return baseProfiles;
   }, [visibleProfiles, workerProfilesInfo, userRole, assignedProfileIds]);

    const handleUserToggle = () => {
      const currentIndex = visibleProfiles.findIndex(p => (p.id ?? p.name) === selectedUser);
      const nextIndex = (currentIndex + 1) % visibleProfiles.length;
      setSelectedUser(visibleProfiles[nextIndex].id ?? visibleProfiles[nextIndex].name);
    };

    const currentUserIndex = visibleProfiles.findIndex(p => (p.id ?? p.name) === selectedUser);
    
    // Obtener perfil activo para mostrar en tooltip
    const activeProfile = selectorProfiles.find(p => (p.id ?? p.name) === selectedUser);


   // Rueda amarilla (Ajustes generales): protegida por PIN de administrador.
   // - securityReady=false: no hacer nada (evita bypass antes de confirmar snapshot real).
   // - sin adminPinHash: abrir SettingsModal directamente.
   // - con adminPinHash y admin ya desbloqueado: abrir directamente.
   // - con adminPinHash y admin bloqueado: mostrar PinDialog; solo al acertar se desbloquea y abre.
   const handleSettingsWheelClick = () => {
     if (!securityReady) {
       return; // snapshot de seguridad aÃºn no confirmado: no interpretar como "sin PIN"
     }
     if (userRole === 'worker') {
       toast({
         title: 'Acceso denegado',
         description: 'No tienes acceso a la configuración general.',
         variant: 'destructive',
       });
       return;
     }
     if (!adminPinHash) {
       setIsSettingsOpen(true);
       return;
     }
     if (isAdminUnlocked) {
       setIsSettingsOpen(true);
       return;
     }
     setIsAdminPinDialogOpen(true);
   };

   const handleAdminPinSuccess = () => {
     unlockAdmin();
     setIsSettingsOpen(true);
   };

   const handleAdminPinRecoverySuccess = () => {
     unlockAdmin();
     setIsSettingsOpen(true);
   };


   const handlePinSuccess = ({ trustDevice = false } = {}) => {
     const profileId = pinDialogProfile?.id ?? pinDialogProfile?.name;
     if (!profileId) return;

      if (trustDevice) {
        const pinUpdatedAt = pinDialogProfile?.pinUpdatedAt;
        const trustedWorkspaceId = workspaceId;

       if (trustedWorkspaceId) {
         registerTrustedDevice(trustedWorkspaceId, db, profileId, pinUpdatedAt)
           .then((success) => {
             if (!success) {
               console.error('[APP_HEADER] Failed to register trusted device');
             }
           })
           .catch((error) => {
             console.error('[APP_HEADER] Error registering trusted device:', error);
           });
       } else {
         console.error('[APP_HEADER] Cannot register trusted device: no authenticated user');
       }
     }

     switchProfile(profileId, { alreadyUnlocked: true });
     setIsPinDialogOpen(false);
     setPinDialogProfile(null);
   };

   // VerificaciÃ³n server-side del PIN de un perfil AJENO/informativo. No
   // conoce ni recibe pinHash/pinSalt (nunca llegan al cliente para estos
   // perfiles): delega toda la comprobaciÃ³n en la callable
   // verifyCrossProfilePin, que reutiliza la misma autorizaciÃ³n de
   // getWorkerProfilesInfo. Devuelve boolean; deja pasar el error de
   // resource-exhausted (bloqueo) para que PinDialog lo maneje.
   const verifyCrossProfilePinFn = async (pin) => {
     const profileId = pinDialogProfile?.id ?? pinDialogProfile?.name;
     const functions = getFunctions();
     const callable = httpsCallable(functions, 'verifyCrossProfilePin');
     const result = await callable({ profileId, pin });
     return result?.data?.valid === true;
   };

   // Cierre de ProfileSettingsModal si el perfil seleccionado es informativo

   useEffect(() => {
     const selectedProfile = selectorProfiles.find(p => p.name === selectedUser);
     if (selectedProfile?.isInformational === true && isProfileSettingsOpen) {
       setIsProfileSettingsOpen(false);
     }
   }, [selectedUser, selectorProfiles, isProfileSettingsOpen]);

   // Flujo ÃšNICO de selecciÃ³n/protecciÃ³n por PIN, reutilizado tanto por la
   // rama de 1-4 perfiles (botones directos) como por ProfileSelector
   // (selector de 5+ perfiles). Ninguna otra ruta debe duplicar esta lÃ³gica.
   //
   // - Perfiles NO informacionales (propios): bloqueo determinado por
   //   isProfileLocked(profileId); si estÃ¡n bloqueados, se intenta primero
   //   trusted device y, si falla, se abre PinDialog.
   // - Perfiles informacionales (ajenos, vÃ­a getWorkerProfilesInfo): bloqueo
   //   determinado por el booleano no sensible profile.hasPin; si estÃ¡n
   //   bloqueados, NUNCA se intenta trusted device (no aplica a perfiles
   //   ajenos) y se abre PinDialog directamente, que usarÃ¡
   //   verifyCrossProfilePinFn para la verificaciÃ³n server-side.
   const attemptProfileSwitch = async (profile) => {
     const profileId = profile.id ?? profile.name;
     const isInformational = profile.isInformational === true;
     const isLocked = isInformational
       ? profile.hasPin === true
       : isProfileLocked(profileId);

     if (trustedDeviceChecking) return;

     if (isLocked) {
       if (!isInformational) {
         const trusted = await tryUnlockWithTrustedDevice(profileId);
         if (trusted) {
           switchProfile(profileId, { alreadyUnlocked: true });
           return;
         }
       }
       setPinDialogProfile(profile);
       setIsPinDialogOpen(true);
       return;
     }

     switchProfile(profileId);
   };

  return (

    <>
      <header className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur-sm overflow-x-hidden">
            {/* FILA 2: Nombre de Empresa/Logo + BotÃ³n Tema + BotÃ³n Ajustes + Campana */}
               <div className="relative flex items-center w-full pl-2 pr-0 pt-5 pb-1 justify-between">
              {/* Izquierda: Tema */}
              <div className="flex items-center gap-2">
                {isInitialized && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleTheme}
                    className={theme === 'dark' ? 'text-yellow-500 hover:bg-transparent hover:text-yellow-500 h-11 w-11 min-h-11 min-w-11' : 'text-primary hover:bg-transparent hover:text-primary h-11 w-11 min-h-11 min-w-11'}
                    aria-label={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
                    title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-5 w-5" />
                    ) : (
                      <Moon className="h-5 w-5" />
                    )}
                  </Button>
                )}
              </div>

              {/* Centro: TÃ­tulo/Logo + Badge Pro */}
              <div className="flex items-center justify-center min-w-0 absolute left-1/2 -translate-x-1/2 px-4">
                <div className="relative w-fit max-w-full">
                  {config.companyLogo ? (
                    <img 
                      src={config.companyLogo} 
                      alt="Logo" 
                      className="h-10 w-auto object-contain max-w-[150px]"
                    />
                  ) : config.companyName ? (
                    <div className="text-xl sm:text-2xl font-bold text-foreground truncate">
                      {config.companyName}
                    </div>
                  ) : (
                    <div className="text-xl sm:text-2xl font-bold text-foreground truncate">
                      RUTAPP
                    </div>
                  )}
                   <div 
                     className="absolute left-full ml-1"
                     style={{
                       top: '50%',
                       transform: 'translateY(calc(-50% - 10px))'
                     }}
                   >
                       <PlanStatusBadge />
                     </div>
                </div>
              </div>

              {/* Derecha: Cuenta (sustituye al engranaje de Ajustes generales) */}
              <Popover open={isAccountOpen} onOpenChange={setIsAccountOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-primary/10 h-11 w-11 min-h-11 min-w-11"
                    aria-label="Abrir Cuenta"
                    disabled={!securityReady}
                  >
                    <User className="h-5 w-5 text-primary" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-72 max-w-[90vw] p-4">
                  <div className="space-y-3">
                    <h3 className="font-semibold text-base leading-none">Cuenta</h3>
                    <div className="space-y-1 text-sm">
                      <p className="truncate">
                        <span className="font-medium">Correo:</span>{' '}
                        {auth.currentUser?.email || 'Correo no disponible'}
                      </p>
                      {userRole != null && (
                        <p>
                          <span className="font-medium">Rol:</span>{' '}
                          {userRole === 'worker' ? 'User' : 'Admin'}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 pt-2 border-t border-border">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsAccountOpen(false);
                          setIsLogoutConfirmOpen(true);
                        }}
                        className="justify-start"
                        aria-label="Cerrar sesión"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Cerrar sesión
                      </Button>
                      {userRole !== 'worker' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsAccountOpen(false);
                            handleSettingsWheelClick();
                          }}
                          className="justify-start"
                          aria-label="Ajustes generales"
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          Ajustes generales
                        </Button>
                      )}
                      {auth.currentUser?.uid === 'wEaPpq9uPZWSoYXCMsGaE4GTumd2' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setIsAccountOpen(false);
                            setIsCompanySearchOpen(true);
                          }}
                          className="justify-start"
                          aria-label="Buscar empresa"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Buscar empresa
                        </Button>
                      )}
                       <div className="border-t border-border pt-2 flex flex-col gap-2">
                         <a
                           href="mailto:soporte@misaplicaciones.es?subject=Rutapp%20-%20Problema%20t%C3%A9cnico"
                           onClick={() => setIsAccountOpen(false)}
                           className="inline-block"
                         >
                           <Button
                             variant="outline"
                             size="sm"
                             className="justify-start w-full"
                             aria-label="Reportar un problema"
                           >
                             <Mail className="h-4 w-4 mr-2" />
                             Reportar un problema
                           </Button>
                         </a>
                         <a
                           href="mailto:soporte@misaplicaciones.es?subject=Rutapp%20-%20Sugerencia"
                           onClick={() => setIsAccountOpen(false)}
                           className="inline-block"
                         >
                           <Button
                             variant="outline"
                             size="sm"
                             className="justify-start w-full"
                             aria-label="Enviar una sugerencia"
                           >
                             <Mail className="h-4 w-4 mr-2" />
                             Enviar una sugerencia
                           </Button>
                         </a>
                         <a
                           href="mailto:soporte@misaplicaciones.es?subject=Rutapp%20-%20Consulta%20de%20soporte"
                           onClick={() => setIsAccountOpen(false)}
                           className="inline-block"
                         >
                           <Button
                             variant="outline"
                             size="sm"
                             className="justify-start w-full"
                             aria-label="Contactar con soporte"
                           >
                             <Mail className="h-4 w-4 mr-2" />
                             Contactar con soporte
                           </Button>
                         </a>
                       </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>

           </div>

                {/* FILA 1: Perfiles - Full width flex-nowrap */}
                <div className="w-full px-4 pt-2 pb-3 flex items-center justify-center relative -mt-[7px]">
                <div
                  className={cn(
                    "flex items-center justify-center flex-nowrap gap-1.5",
                    selectorProfiles.length === 4 && "-translate-x-2"
                  )}
                >
                   {/* LÃ³gica condicional: 1-4 perfiles vs 5+ perfiles */}
                   {selectorProfiles.length < 5 ? (
                   // Modo 1-4 perfiles: mostrar todos
                   <>
                     {selectorProfiles.map((profile, index) => {
                      const isSelected = (profile.id ?? profile.name) === selectedUser;
                      const profileId = profile.id ?? profile.name;
                      
                      const rawStatus = routeStatus?.[profileId];
                      const isOtherProfile = profileId !== selectedUser;
                      const canSeeSharedStatus = !isOtherProfile || permissions.canUseSharedRouteStates;
                      const profileStatus =
                        colorCodeEnabled && canSeeSharedStatus && typeof rawStatus === 'string' && rawStatus.trim().length > 0
                          ? rawStatus
                          : null;

                       const statusColor = profileStatus ? getStatusColor(profileStatus) : null;
                          const isInformational = profile.isInformational === true;
                          // Perfiles informativos (ajenos, vÃ­a getWorkerProfilesInfo) no existen en
                          // config.profiles, por lo que isProfileLocked(profileId) siempre devolverÃ­a
                          // false para ellos (ausencia de pinHash local != "sin PIN"). Se usa el
                          // booleano no sensible hasPin devuelto por la Cloud Function para decidir
                          // el bloqueo de estos perfiles ajenos; los perfiles propios siguen usando
                          // isProfileLocked como hasta ahora.
                          const isLocked = isInformational
                            ? profile.hasPin === true
                            : isProfileLocked(profileId);


                          // [RT_RENDER] InstrumentaciÃ³n temporal para diagnosticar color del perfil
                          console.log("[RT_RENDER]", {
                            profileId,
                            selectedUser,
                            rawStatus,
                            profileStatus,
                            colorCodeEnabled,
                            canSeeSharedStatus,
                            colorFinal: statusColor || 'none'
                          });

                          // [TEMP_SARA_DEBUG] InstrumentaciÃ³n temporal para diagnosticar routeStatus de Sara
                          if (profile.name === "Sara") {
                            console.log("[TEMP_SARA_DEBUG] SARA render debug:", {
                              profileId,
                              profileName: profile.name,
                              isInformational,
                              rawStatus,
                              colorCodeEnabled,
                              canSeeSharedStatus,
                              profileStatus,
                              statusColor,
                              routeStatusKeys: Object.keys(routeStatus || {}),
                              routeStatusObject: routeStatus
                            });
                          }

                          // [TEMP_DAVID_DEBUG] InstrumentaciÃ³n temporal para diagnosticar routeStatus
                          if (profileId === "89cb72c5-c543-4022-84fb-68ab7e5fdeb7") {
                            console.log("[TEMP_DAVID_DEBUG] DAVID observed by SARA:", {
                              profileId,
                              routeStatusKeys: Object.keys(routeStatus || {}),
                              rawStatus,
                              colorCodeEnabled,
                              profileStatus,
                              statusColor,
                              isInformational,
                              isSelected
                            });
                          }

                          const handleProfileClick = async () => {
                        await attemptProfileSwitch(profile);
                      };


                      return (
                        <div key={index} style={{ position: 'relative', width: '64px', height: '64px' }}>
                           <button
                               className={cn(
                                 'p-1 flex flex-col items-center justify-center gap-0 transition-transform duration-150 ease-out w-full h-full',
                                 isSelected && selectorProfiles.length > 1 ? 'scale-[1.15] z-10' : 'scale-100'
                               )}
                             aria-label={`Seleccionar ${profile.name}${isLocked ? ' (protegido)' : ''}`}
                             onClick={() => {
                               const canConsultInformational =
                                 userRole === 'worker' && config.allowCrossProfileClientsConsultation === true;
                               if (isInformational && !canConsultInformational) {
                                 return;
                               }
                               handleProfileClick();
                             }}
                             disabled={trustedDeviceChecking}
                            title={profile.name}
                          >
                            <div style={{ position: 'relative', width: '62px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {profileStatus && (
                                <div
                                  className="absolute pointer-events-none"
                                  aria-hidden="true"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: getDisplayColor(profileStatus, theme, statusColors),
                                    WebkitMaskImage: `url(${MaskPng})`,
                                    maskImage: `url(${MaskPng})`,
                                    WebkitMaskSize: 'contain',
                                    maskSize: 'contain',
                                    WebkitMaskPosition: 'center',
                                    maskPosition: 'center',
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskRepeat: 'no-repeat'
                                  }}
                                />
                              )}
                              <img
                                src={
                                  isSelected
                                    ? (theme === 'dark' ? VanDarkOnPng : VanLightOnPng)
                                    : VanPng
                                }
                                alt="van icon"
                                className="object-contain select-none"
                                style={{ maxWidth: '100%', maxHeight: '100%' }}
                              />
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '2.5px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.875rem',
                                  fontWeight: '500',
                                  color: '#000000',
                                  whiteSpace: 'nowrap',
                                  textOverflow: 'ellipsis',
                                  letterSpacing: '-0.5px',
                                  maxWidth: '52px'
                                }}
                              >
                                {profile.name.length > 8 
                                  ? (profile.name.substring(0, 7).charAt(0).toUpperCase() + profile.name.substring(0, 7).slice(1).toLowerCase())
                                  : (profile.name.charAt(0).toUpperCase() + profile.name.slice(1).toLowerCase())
                                }
                              </div>
                            </div>
                          </button>
                           
                           {isSelected && !isLocked && !isInformational && (
                             <button
                               onClick={(e) => {
                                 e.stopPropagation();
                                 setIsProfileSettingsOpen(true);
                               }}
                              className="absolute -top-2 -right-2 rounded-full p-0 border border-primary transition-colors bg-white hover:bg-white/90"
                              style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              aria-label={`Configuración de ${profile.name}`}
                              title={`Configuración de ${profile.name}`}
                            >
                              <Settings className="h-3.5 w-3.5 text-primary" />
                            </button>
                           )}

                           {!isSelected && config.shareWhatsAppAccess && profile.whatsapp && profile.whatsapp.trim() !== '' && (
                            <div onClick={(e) => e.stopPropagation()}>
                              <MiniWhatsAppButton profile={profile} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Diï¿½logo de confirmaciï¿½n para cerrar sesiï¿½n */}
         <AlertDialog open={isLogoutConfirmOpen} onOpenChange={(open) => { if (!open) setIsLogoutConfirmOpen(false); }}>
           <AlertDialogContent>
             <AlertDialogHeader>
               <AlertDialogTitle>Confirmar cierre de sesiï¿½n</AlertDialogTitle>
               <AlertDialogDescription>
                 ï¿½Seguro que quieres cerrar sesiï¿½n?
               </AlertDialogDescription>
             </AlertDialogHeader>
             <AlertDialogFooter>
               <AlertDialogCancel>Cancelar</AlertDialogCancel>
               <AlertDialogAction
                 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                 onClick={() => { setIsLogoutConfirmOpen(false); handleLogout(); }}
               >
                 Sï¿½, cerrar sesiï¿½n
               </AlertDialogAction>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>       </>
                ) : (
                  // Modo 5+ perfiles: mostrar solo perfil activo + botÃ³n "+"
                  <>
                    {activeProfile && (
                      <div style={{ position: 'relative', width: '64px', height: '64px' }}>
                        <button
                          className={cn(
                            'p-1 flex flex-col items-center justify-center gap-0 transition-transform duration-150 ease-out w-full h-full',
                            'scale-[1.15] z-10'
                          )}
                          aria-label={`Seleccionar ${activeProfile.name}${isProfileLocked(activeProfile.id ?? activeProfile.name) ? ' (protegido)' : ''}`}
                          title={activeProfile.name}
                          disabled={true}
                        >
                          <div style={{ position: 'relative', width: '62px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {(() => {
                              const profileId = activeProfile.id ?? activeProfile.name;
                              const rawStatus = routeStatus?.[profileId];
                              const profileStatus =
                                colorCodeEnabled && typeof rawStatus === 'string' && rawStatus.trim().length > 0
                                  ? rawStatus
                                  : null;
                              return profileStatus && (
                                <div
                                  className="absolute pointer-events-none"
                                  aria-hidden="true"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: getDisplayColor(profileStatus, theme, statusColors),
                                    WebkitMaskImage: `url(${MaskPng})`,
                                    maskImage: `url(${MaskPng})`,
                                    WebkitMaskSize: 'contain',
                                    maskSize: 'contain',
                                    WebkitMaskPosition: 'center',
                                    maskPosition: 'center',
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskRepeat: 'no-repeat'
                                  }}
                                />
                              );
                            })()}
                            <img
                              src={theme === 'dark' ? VanDarkOnPng : VanLightOnPng}
                              alt="van icon"
                              className="object-contain select-none"
                              style={{ maxWidth: '100%', maxHeight: '100%' }}
                            />
                            <div
                              style={{
                                position: 'absolute',
                                bottom: '2.5px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: '#000000',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                letterSpacing: '-0.5px',
                                maxWidth: '52px'
                              }}
                            >
                              {activeProfile.name.length > 8 
                                ? (activeProfile.name.substring(0, 7).charAt(0).toUpperCase() + activeProfile.name.substring(0, 7).slice(1).toLowerCase())
                                : (activeProfile.name.charAt(0).toUpperCase() + activeProfile.name.slice(1).toLowerCase())
                              }
                            </div>
                          </div>
                        </button>
                        
                        {!isProfileLocked(activeProfile.id ?? activeProfile.name) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsProfileSettingsOpen(true);
                            }}
                            className="absolute -top-2 -right-2 rounded-full p-0 border border-primary transition-colors bg-white hover:bg-white/90"
                            style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            aria-label={`Configuración de ${activeProfile.name}`}
                            title={`Configuración de ${activeProfile.name}`}
                          >
                            <Settings className="h-3.5 w-3.5 text-primary" />
                          </button>
                        )}
                      </div>
                    )}

                    {/* BotÃ³n "+" para abrir selector */}
                    <button
                      onClick={() => setIsProfileSelectorOpen(true)}
                      className="flex items-center justify-center rounded-full h-12 w-12 transition-colors shadow-md bg-white dark:bg-white border border-primary hover:bg-primary/5"
                      aria-label="Seleccionar otro perfil"
                      title="Seleccionar perfil"
                    >
                      <Plus className="h-6 w-6 text-primary" strokeWidth={3} />
                    </button>
                  </>
                )}
                </div>

                {/* Campana: posicionada al extremo derecho, fuera del cÃ¡lculo de centrado */}
                 <div className="absolute right-0 top-[calc(50%_-_6px)] -translate-y-1/2">
                  {showAlertButton && (
                    <Button
                      onClick={onAlertClick}
                      size="sm"
                      variant="ghost"
                      aria-label="Abrir avisos entre perfiles"
                      className="h-10 w-10 p-0 flex items-center justify-center bg-transparent hover:bg-transparent active:bg-transparent border-0 shadow-none rounded-none"
                    >
                      <div className="relative w-full h-full flex items-center justify-center">
                        <BellRing className="h-[27px] w-[27px]" stroke={theme === 'dark' ? '#ffcc00' : '#000000'} fill="#fff191" strokeWidth={0.8} />
                      </div>
                    </Button>
                  )}
                </div>
              </div>

        {/* Barra de Estados de Ruta */}
        {colorCodeEnabled && (
          <RouteStatusBar
            currentStatus={routeStatus}
            onStatusChange={onRouteStatusChange}
            currentDate={currentDate}
            selectedUser={selectedUser}
            profileName={activeProfile?.name || null}
            statusLabels={statusLabels}
            requireConfirmation={requireConfirmation}
            profileUuid={config.profiles?.find(p => p.name === selectedUser)?.id || null}
          />
        )}

       </header>

       <SettingsModal
         isOpen={isSettingsOpen}
         onClose={() => setIsSettingsOpen(false)}
         workspaceId={workspaceId}
         userRole={userRole}
         onConfigureProfilePin={(profileId) => {
           setIsSettingsOpen(false);
           switchProfile(profileId, { alreadyUnlocked: true });
           setIsProfileSettingsOpen(true);
         }}
       />
       
       <ProfileSettingsModal 
         open={isProfileSettingsOpen} 
         onOpenChange={setIsProfileSettingsOpen}
         selectedUser={selectedUser}
         profiles={config.profiles}
         updateProfilesConfig={updateProfilesConfig}
         updateOwnWorkerProfile={updateOwnWorkerProfile}
         userRole={userRole}
         workspaceId={workspaceId}
       />

        {/* DiÃ¡logo de PIN para desbloquear la rueda amarilla (Ajustes generales) */}
        <PinDialog
          open={isAdminPinDialogOpen}
          onOpenChange={setIsAdminPinDialogOpen}
          profileName="Administrador"
          pinHash={adminPinHash}
          pinSalt={adminPinSalt}
          onSuccess={handleAdminPinSuccess}
          showForgotPin={true}
          onForgotPin={() => {
            setIsAdminPinDialogOpen(false);
            setIsAdminPinRecoveryDialogOpen(true);
          }}
        />

        {/* DiÃ¡logo de recuperaciÃ³n del PIN de administrador vÃ­a contraseÃ±a de acceso */}
        <AdminPinRecoveryDialog
          open={isAdminPinRecoveryDialogOpen}
          onOpenChange={setIsAdminPinRecoveryDialogOpen}
          workspaceId={workspaceId}
          updateAdminSecurityConfig={updateAdminSecurityConfig}
          onSuccess={handleAdminPinRecoverySuccess}
        />


        {/* DiÃ¡logo de PIN para desbloquear perfiles protegidos */}
        <PinDialog
          open={isPinDialogOpen}
          onOpenChange={setIsPinDialogOpen}
          profileName={pinDialogProfile?.name}
          pinHash={pinDialogProfile?.pinHash}
          pinSalt={pinDialogProfile?.pinSalt}
          onSuccess={handlePinSuccess}
          showTrustDeviceOption={pinDialogProfile?.isInformational !== true}
          verifyFn={pinDialogProfile?.isInformational === true ? verifyCrossProfilePinFn : undefined}
          showForgotPin={
            userRole === 'worker' &&
            Array.isArray(assignedProfileIds) &&
            !!pinDialogProfile &&
            pinDialogProfile.isInformational !== true &&
            assignedProfileIds.includes(pinDialogProfile.id ?? pinDialogProfile.name)
          }
          onForgotPin={() => {
            setIsPinDialogOpen(false);
            setIsProfilePinRecoveryDialogOpen(true);
          }}
        />


        {/* DiÃ¡logo de recuperaciÃ³n simple del PIN de perfil propio (solo worker asignado) */}
        <ProfilePinRecoveryDialog
          open={isProfilePinRecoveryDialogOpen}
          onOpenChange={setIsProfilePinRecoveryDialogOpen}
          profileId={pinDialogProfile?.id ?? pinDialogProfile?.name}
          profileName={pinDialogProfile?.name}
          updateOwnWorkerProfile={updateOwnWorkerProfile}
          onSuccess={() => {
            setPinDialogProfile(null);
          }}
        />

         {/* ProfileSelector: Modal para seleccionar entre 5+ perfiles */}
         <ProfileSelector
           open={isProfileSelectorOpen}
           onOpenChange={setIsProfileSelectorOpen}
           profiles={selectorProfiles}
           selectedUser={selectedUser}
           routeStatus={routeStatus}
           colorCodeEnabled={colorCodeEnabled}
           onProfileSelect={attemptProfileSwitch}
           isProfileLocked={isProfileLocked}
           permissions={permissions}

           userRole={userRole}
            statusColors={statusColors}

           allowCrossProfileClientsConsultation={config.allowCrossProfileClientsConsultation === true}
         />

         {/* DiÃ¡logo de confirmaciÃ³n para cerrar sesión */}
         <AlertDialog open={isLogoutConfirmOpen} onOpenChange={(open) => { if (!open) setIsLogoutConfirmOpen(false); }}>
           <AlertDialogContent>
             <AlertDialogHeader>
               <AlertDialogTitle>Confirmar cierre de sesión</AlertDialogTitle>
               <AlertDialogDescription>
                 ¿Seguro que quieres cerrar sesión?
               </AlertDialogDescription>
             </AlertDialogHeader>
             <AlertDialogFooter>
               <AlertDialogCancel>Cancelar</AlertDialogCancel>
               <AlertDialogAction
                 className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                 onClick={() => { setIsLogoutConfirmOpen(false); handleLogout(); }}
               >
                 Sí­, cerrar sesión
               </AlertDialogAction>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>

         {/* Modal de búsqueda de empresas (solo para cuenta interna) */}
         <InternalCompanySearchModal
           isOpen={isCompanySearchOpen}
           onOpenChange={setIsCompanySearchOpen}
         />
      </>
   );
 }

